/**
 * Event Modal Components for Calendar
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { X, Save, CheckCircle, Plus, Trash2, Search, Users } from 'lucide-react';
import { CalendarEvent, AttendeeUser, calendarAPI, suppliersAPI, SupplierProfile } from '../../lib/api';
import { CREATABLE_EVENT_TYPES, EVENT_TYPE_LABELS } from './constants';
import { getStatusBadgeClass, toLocalDateTimeInput } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';

interface EventDetailModalProps {
  open: boolean;
  event: CalendarEvent | null;
  isEditing: boolean;
  editForm: Partial<CalendarEvent>;
  onClose: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onComplete: () => void;
  onDelete: () => void;
  onFormChange: (form: Partial<CalendarEvent>) => void;
  /** When true, hides edit/delete/complete action buttons */
  readOnly?: boolean;
}

export function EventDetailModal({
  open,
  event,
  isEditing,
  editForm,
  onClose,
  onEdit,
  onCancelEdit,
  onSave,
  onComplete,
  onDelete,
  onFormChange,
  readOnly = false,
}: EventDetailModalProps) {
  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{isEditing ? 'Edit Event' : 'Event Details'}</span>
            <Badge className={getStatusBadgeClass(event.status)}>
              {event.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update event information' : 'View and manage event details'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEditing ? (
            <EventViewContent event={event} />
          ) : (
            <EventEditForm editForm={editForm} onFormChange={onFormChange} />
          )}
        </div>

        <DialogFooter className="flex justify-between">
          {!isEditing ? (
            <>
              {!readOnly && (
                <Button variant="destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  <X className="h-4 w-4 mr-2" />
                  Close
                </Button>
                {!readOnly && event.status !== 'completed' && (
                  <>
                    <Button variant="outline" onClick={onEdit}>
                      Edit
                    </Button>
                    <Button onClick={onComplete}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Complete Event
                    </Button>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div />
              <div className="flex gap-2">
                <Button variant="outline" onClick={onCancelEdit}>
                  Cancel
                </Button>
                <Button onClick={onSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventViewContent({ event }: { event: CalendarEvent }) {
  const { formatDateTime } = useFormatters();

  return (
    <>
      <div>
        <Label>Title</Label>
        <p className="text-lg font-semibold mt-1">{event.title}</p>
      </div>
      {event.description && (
        <div>
          <Label>Description</Label>
          <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Event Type</Label>
          <Badge variant="outline" className="mt-1">
            {EVENT_TYPE_LABELS[event.event_type]}
          </Badge>
        </div>
        <div>
          <Label>All Day</Label>
          <p className="text-sm mt-1">{event.all_day ? 'Yes' : 'No'}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Date/Time</Label>
          <p className="text-sm mt-1">{formatDateTime(event.start_datetime)}</p>
        </div>
        <div>
          <Label>End Date/Time</Label>
          <p className="text-sm mt-1">{formatDateTime(event.end_datetime)}</p>
        </div>
      </div>
      {event.location && (
        <div>
          <Label>Location</Label>
          <p className="text-sm mt-1">{event.location}</p>
        </div>
      )}
      {event.reference_id && (
        <div>
          <Label>Reference ID</Label>
          <p className="text-sm font-mono mt-1">{event.reference_id}</p>
        </div>
      )}
    </>
  );
}

interface EventEditFormProps {
  editForm: Partial<CalendarEvent>;
  onFormChange: (form: Partial<CalendarEvent>) => void;
}

function EventEditForm({ editForm, onFormChange }: EventEditFormProps) {
  return (
    <>
      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          value={editForm.title || ''}
          onChange={(e) => onFormChange({ ...editForm, title: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={editForm.description || ''}
          onChange={(e) => onFormChange({ ...editForm, description: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Date/Time</Label>
          <Input
            type="datetime-local"
            value={
              editForm.start_datetime
                ? toLocalDateTimeInput(editForm.start_datetime)
                : ''
            }
            onChange={(e) =>
              onFormChange({
                ...editForm,
                start_datetime: new Date(e.target.value).toISOString(),
              })
            }
          />
        </div>
        <div className="space-y-2">
          <Label>End Date/Time</Label>
          <Input
            type="datetime-local"
            value={
              editForm.end_datetime
                ? toLocalDateTimeInput(editForm.end_datetime)
                : ''
            }
            onChange={(e) =>
              onFormChange({
                ...editForm,
                end_datetime: new Date(e.target.value).toISOString(),
              })
            }
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Location</Label>
        <Input
          value={editForm.location || ''}
          onChange={(e) => onFormChange({ ...editForm, location: e.target.value })}
        />
      </div>
    </>
  );
}

interface CreateEventModalProps {
  open: boolean;
  editForm: Partial<CalendarEvent> & { attendee_ids?: number[] };
  onClose: () => void;
  onCreate: () => void;
  onFormChange: (form: Partial<CalendarEvent> & { attendee_ids?: number[] }) => void;
}

export function CreateEventModal({
  open,
  editForm,
  onClose,
  onCreate,
  onFormChange,
}: CreateEventModalProps) {
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<AttendeeUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<AttendeeUser[]>([]);
  const [searching, setSearching] = useState(false);

  // Load suppliers on mount
  useEffect(() => {
    if (open) {
      suppliersAPI.getProfiles({ page_size: 1000 }).then((res) => {
        setSuppliers(res.data.results || []);
      }).catch(() => {});
      // Reset attendee state
      setSelectedUsers([]);
      setUserSearch('');
      setSearchResults([]);
    }
  }, [open]);

  // Search users with debounce
  const handleUserSearch = useCallback(async (query: string) => {
    setUserSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearching(true);
      const params: { search: string } = { search: query };
      const res = await calendarAPI.searchUsers(params);
      // Filter out already-selected users
      const selectedIds = new Set(selectedUsers.map((u) => u.id));
      setSearchResults(res.data.filter((u) => !selectedIds.has(u.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [selectedUsers]);

  const handleAddUser = (user: AttendeeUser) => {
    const updated = [...selectedUsers, user];
    setSelectedUsers(updated);
    onFormChange({ ...editForm, attendee_ids: updated.map((u) => u.id) });
    setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
    setUserSearch('');
  };

  const handleRemoveUser = (userId: number) => {
    const updated = selectedUsers.filter((u) => u.id !== userId);
    setSelectedUsers(updated);
    onFormChange({ ...editForm, attendee_ids: updated.map((u) => u.id) });
  };

  // When supplier changes, clear users and re-search if there's a query
  const handleSupplierChange = (value: string) => {
    const supplierId = value === 'none' ? null : Number(value);
    onFormChange({ ...editForm, supplier: supplierId });
    setSelectedUsers([]);
    onFormChange({ ...editForm, supplier: supplierId, attendee_ids: [] });
    setUserSearch('');
    setSearchResults([]);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Event</DialogTitle>
          <DialogDescription>Add a new event to the calendar</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={editForm.title || ''}
              onChange={(e) => onFormChange({ ...editForm, title: e.target.value })}
              placeholder="Event title"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={editForm.description || ''}
              onChange={(e) => onFormChange({ ...editForm, description: e.target.value })}
              placeholder="Event description (optional)"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select
                value={editForm.event_type || 'meeting'}
                onValueChange={(value) =>
                  onFormChange({ ...editForm, event_type: value as CalendarEvent['event_type'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREATABLE_EVENT_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {EVENT_TYPE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editForm.status || 'scheduled'}
                onValueChange={(value) =>
                  onFormChange({ ...editForm, status: value as CalendarEvent['status'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Supplier Selection */}
          <div className="space-y-2">
            <Label>Supplier</Label>
            <Select
              value={editForm.supplier ? String(editForm.supplier) : 'none'}
              onValueChange={handleSupplierChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a supplier (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supplier</SelectItem>
                {suppliers
                  .filter((s) => (s.company_id ?? s.company) != null)
                  .map((s) => (
                    <SelectItem key={s.id} value={String(s.company_id ?? s.company)}>
                      {s.company_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Attendee / User Search */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Attendees
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={userSearch}
                onChange={(e) => handleUserSearch(e.target.value)}
                placeholder="Search users by name or email..."
                className="pl-9"
              />
            </div>
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="border rounded-md max-h-40 overflow-y-auto bg-background shadow-sm">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between text-sm"
                    onClick={() => handleAddUser(user)}
                  >
                    <div>
                      <span className="font-medium">{user.full_name}</span>
                      <span className="text-muted-foreground ml-2">{user.email}</span>
                    </div>
                    {user.company_name && (
                      <Badge variant="outline" className="text-xs ml-2">{user.company_name}</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
            {searching && <p className="text-xs text-muted-foreground">Searching...</p>}
            {userSearch.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-xs text-muted-foreground">No users found</p>
            )}
            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedUsers.map((user) => (
                  <Badge key={user.id} variant="secondary" className="flex items-center gap-1 pr-1">
                    {user.full_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="ml-1 hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date/Time *</Label>
              <Input
                type="datetime-local"
                value={
                  editForm.start_datetime
                    ? toLocalDateTimeInput(editForm.start_datetime)
                    : ''
                }
                onChange={(e) =>
                  onFormChange({
                    ...editForm,
                    start_datetime: new Date(e.target.value).toISOString(),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>End Date/Time *</Label>
              <Input
                type="datetime-local"
                value={
                  editForm.end_datetime
                    ? toLocalDateTimeInput(editForm.end_datetime)
                    : ''
                }
                onChange={(e) =>
                  onFormChange({
                    ...editForm,
                    end_datetime: new Date(e.target.value).toISOString(),
                  })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Location</Label>
            <Input
              value={editForm.location || ''}
              onChange={(e) => onFormChange({ ...editForm, location: e.target.value })}
              placeholder="Event location (optional)"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onCreate} disabled={!editForm.title}>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
